<template>
  <div class="leak-investigation-reports-search-filters">
    <b-card class="bg-light shadow-sm border-0 rounded-xl">      
      <b-form
        class="d-flex justify-content-between"
        @submit.prevent="handleSearch"
      >
        <b-row class="flex-fill mr-3">
          <b-col
            md="12"
            lg="4"
          >
            <b-form-group>
              <b-form-select
                id="tenant-id"
                v-model="localFilters.tenantId"
                :options="tenantOptions"
                class="rounded-pill"
                @input="handleTenantChange"
              >
                <template #first>
                  <b-form-select-option :value="null">
                    All Tenants
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col> 

          <b-col
            md="12"
            lg="4"
          >
            <b-form-group
              label="From"
              horizontal
              label-for="date-from"
              label-cols="2"
            >
              <b-form-input
                id="date-from"
                v-model="localFilters.dateFrom"
                type="date"
                :disabled="loading"
                class="rounded-pill"
                @input="onFilterChange"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="12"
            lg="4"
          >
            <b-form-group
              label="To"
              horizontal
              label-for="date-to"
              label-cols="2"
            >
              <b-form-input
                id="date-to"
                v-model="localFilters.dateTo"
                type="date"
                :disabled="loading"
                class="rounded-pill"
                @input="onFilterChange"
              />
            </b-form-group>
          </b-col>

          <b-col
            md="12"
            lg="4"
          >
            <b-form-group
              label="LeakBot ID"
              horizontal
              label-for="leakbot-search"
              label-cols="2"
            >
              <b-input-group>
                <b-form-input
                  id="leakbot-search"
                  v-model="localFilters.leakbotId"
                  placeholder="Enter LeakBot ID..."
                  :disabled="loading"
                  class="rounded-pill"
                  @input="onFilterChange"
                />
                <b-input-group-button
                  v-if="localFilters.leakbotId"
                  slot="right"
                  class="ml-3"
                >
                  <b-button
                    variant="outline-secondary"
                    @click="clearLeakBotFilter"
                  >
                    <i class="fa fa-times" />
                  </b-button>
                </b-input-group-button>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col
            md="12"
            lg="4"
          >
            <b-form-group
              label="Partner Reference"
              horizontal
              label-for="partner-ref-search"
            >
              <b-input-group>
                <b-form-input
                  id="partner-ref-search"
                  v-model="localFilters.partnerReference"
                  placeholder="Enter Partner Reference..."
                  :disabled="loading"
                  class="rounded-pill"
                  @input="onFilterChange"
                />
                <b-input-group-button
                  v-if="localFilters.partnerReference"
                  slot="right"
                  class="ml-3"
                >
                  <b-button
                    variant="outline-secondary"
                    @click="clearPartnerReferenceFilter"
                  >
                    <i class="fa fa-times" />
                  </b-button>
                </b-input-group-button>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col
            md="12"
            lg="4"
          >
            <b-form-group
              label="Work Order Number"
              horizontal
              label-for="work-order-search"
            >
              <b-input-group>
                <b-form-input
                  id="work-order-search"
                  v-model="localFilters.workOrderNumber"
                  placeholder="Enter Work Order Number..."
                  :disabled="loading"
                  class="rounded-pill"
                  @input="onFilterChange"
                />
                <b-input-group-button
                  v-if="localFilters.workOrderNumber"
                  slot="right"
                  class="ml-3"
                >
                  <b-button
                    variant="outline-secondary"
                    @click="clearWorkOrderFilter"
                  >
                    <i class="fa fa-times" />
                  </b-button>
                </b-input-group-button>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>

        <div class="ml-3 d-flex flex-column align-items-center">
          <b-button
            :disabled="loading || exportingCsv || totalReports === 0"
            variant="outline-brand-secondary"
            class="font-weight-bold"
            @click="handleExportCsv"
          >
            {{ exportingCsv ? 'Exporting...' : 'Export CSV' }}
            <i
              v-if="exportingCsv"
              class="fa fa-spinner fa-spin me-1"
            />
            <small
              v-else-if="totalReports > 0"
              class="ms-1 font-weight-bold"
            >[{{ totalReports }} records]</small>
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            class="mt-3 font-weight-bold"
            :disabled="loading"
          >
            <span class="px-3">{{ loading ? 'Searching...' : 'Search' }}</span>
          </b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script setup>
import { reactive, computed, watch, onMounted } from 'vue'
import { useTenantSelection } from '../../composables/useTenantSelection.js'

const props = defineProps({
  filters: {
    type: Object,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  exportingCsv: {
    type: Boolean,
    default: false
  },
  totalReports: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['update-filters', 'search', 'export-csv'])

const { tenants, selectedTenantId, setTenant } = useTenantSelection()

const localFilters = reactive({
  tenantId: props.filters.tenantId || null,
  dateFrom: props.filters.dateFrom || null,
  dateTo: props.filters.dateTo || null,
  leakbotId: props.filters.leakbotId || '',
  partnerReference: props.filters.partnerReference || '',
  workOrderNumber: props.filters.workOrderNumber || ''
})

const tenantOptions = computed(() => {
  return tenants.value.map(tenant => ({
    value: tenant.id,
    text: tenant.name || `Tenant ${tenant.id}`
  }))
})

const onFilterChange = () => {
  emit('update-filters', { ...localFilters })
}

const handleSearch = () => {
  emit('search')
}

const handleExportCsv = () => {
  emit('export-csv')
}

const handleTenantChange = (tenant) => {
  setTenant(tenant)
  onFilterChange()
}

const clearPartnerReferenceFilter = () => {
  localFilters.partnerReference = ''
  onFilterChange()
}

const clearWorkOrderFilter = () => {
  localFilters.workOrderNumber = ''
  onFilterChange()
}

const clearLeakBotFilter = () => {
  localFilters.leakbotId = ''
  onFilterChange()
}

onMounted(() => {
  localFilters.tenantId = selectedTenantId.value
})

watch(
  () => props.filters,
  (newFilters) => {
    localFilters.tenantId = newFilters.tenantId || null
    localFilters.dateFrom = newFilters.dateFrom || null
    localFilters.dateTo = newFilters.dateTo || null
    localFilters.leakbotId = newFilters.leakbotId || ''
    localFilters.partnerReference = newFilters.partnerReference || ''
    localFilters.workOrderNumber = newFilters.workOrderNumber || ''
  },
  { deep: true }
)
</script>
